import { FC, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { SubmitStatus } from '@/hooks/useSubmitStatus';

interface LoadingButtonProps {
  status: SubmitStatus;
  type?: 'button' | 'submit';
  idleText: string;
  loadingText?: string;
  successText?: string;
  errorText?: string;
  onClick?: () => void;
  disabled?: boolean;
  isFull?: boolean;
  className?: string;
}

export const LoadingButton: FC<LoadingButtonProps> = ({
  status,
  type = 'button',
  idleText,
  loadingText = 'Loading...',
  successText = 'Success!',
  errorText = 'Error!',
  onClick,
  disabled = false,
  isFull,
  className,
}) => {
  const getText = (): ReactNode => {
    switch (status) {
      case 'loading':
        return loadingText;
      case 'success':
        return successText;
      case 'error':
        return errorText;
      default:
        return idleText;
    }
  };

  const getVariant = () => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'destructive';
      default:
        return 'default';
    }
  };

  return (
    <Button
      type={type}
      variant={getVariant()}
      className={
        isFull
          ? 'w-full'
          : (className ?? 'bg-red-600 hover:bg-red-800 text-white')
      }
      disabled={status === 'loading' || disabled}
      onClick={onClick}
      aria-label="Loading button"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={status}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.2 }}
        >
          {getText()}
        </motion.span>
      </AnimatePresence>
    </Button>
  );
};
